import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAppNavigation } from '../../../hooks/useAppNavigation';
import { useCart } from '../../../hooks/useCart';
import { useMenus } from '../../../hooks/useMenus';

import { ReactComponent as CartIcon } from '../../../icons/cart.svg';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as ChevronRightIcon } from '../../../icons/chevron-right.svg';
import { ReactComponent as CloseIcon } from '../../../icons/close.svg';
import { ReactComponent as SuccessIcon } from '../../../icons/success.svg';

import './create-meal.scss';

import { Currency } from '../../../utils/currency/Currency';
import { hasMacros, MACRO_DEFS, MACRO_NAMES } from '../../../utils/macros';
import { Button } from '../../common/button/Button';
import { FoodCard } from '../../common/food-card/FoodCard';
import { GramSelector } from '../../common/gram-selector/GramSelector';
import { Textarea } from '../../common/textarea/Textarea';
import { QuantitySelector } from '../../quantity-selector/QuantitySelector';

/* ── helpers ──────────────────────────────────────────────────────────────── */

const calcSubTotal = (selections) =>
  Object.values(selections).reduce((sum, { item, grams }) => {
    if (!item) return sum;
    return +(sum + (item.price ?? 0) * grams).toFixed(2);
  }, 0);

const aggregateMacros = (selections) => {
  const totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  Object.values(selections).forEach(({ item, grams }) => {
    if (!item) return;
    MACRO_DEFS.forEach(({ key }) => {
      if (item[key] != null) totals[key] += (item[key] / 100) * grams;
    });
  });
  return totals;
};

const formatMacroValue = (key, value) =>
  key === 'calories' ? `${Math.round(value)}` : `${Math.round(value * 10) / 10}g`;

const RAIL_MACRO_LABELS = { calories: 'kcal', protein: 'Protein', carbs: 'Carbs', fat: 'Fat' };

const hasIncompleteMacros = (selections) =>
  Object.values(selections).some(({ item } = {}) => item && !hasMacros(item));

/* ── MacroTotals (shared strip used by rail + review, mirrors menu-grid-item's macro strip) ── */

const MacroTotals = ({ totals, labels, incomplete }) => (
  <>
    <div className="cmb-macros">
      {MACRO_DEFS.map(({ key, color }) => (
        <div key={key} className="cmb-macros__pill">
          <span style={{ color }}>{formatMacroValue(key, totals[key])}</span>
          <small>{labels[key]}</small>
        </div>
      ))}
    </div>
    {incomplete && (
      <span className="cmb-macros__note">Note: totals exclude items without nutrition info.</span>
    )}
  </>
);

/* ── RailStep ─────────────────────────────────────────────────────────────── */

const RailStep = ({ index, label, state, meta, price, onClick }) => (
  <div
    className={`cmb-rail__step cmb-rail__step--${state}`}
    onClick={state === 'done' ? onClick : undefined}
  >
    <div className="cmb-rail__step-circle">
      {state === 'done' ? <CheckIcon width={13} height={13} /> : index + 1}
    </div>
    <div className="cmb-rail__step-body">
      <span className="cmb-rail__step-label">{label}</span>
      {meta && <span className="cmb-rail__step-meta">{meta}</span>}
    </div>
    {price != null && (
      <span className="cmb-rail__step-price">
        <Currency value={price} />
      </span>
    )}
  </div>
);

/* ── ReviewStep ───────────────────────────────────────────────────────────── */

const ReviewStep = ({
  customFields,
  selections,
  qty,
  setQty,
  subTotal,
  total,
  goToStep,
  instructions,
  setInstructions
}) => {
  const totals = useMemo(() => aggregateMacros(selections), [selections]);

  return (
    <div className="cmb-review">
      <div className="cmb-review__list">
        {customFields.map((field, index) => {
          const { item, grams } = selections[field.id] ?? { item: null, grams: 100 };
          return (
            <div key={field.id} className={`cmb-review__row${!item ? ' empty' : ''}`}>
              <div className="cmb-review__icon">
                {item?.imageUrl || field.imageUrl ? (
                  <img src={item?.imageUrl ?? field.imageUrl} alt="" />
                ) : (
                  <span>{field.name?.[0]}</span>
                )}
              </div>
              <div className="cmb-review__info">
                <div className="cmb-review__name">
                  {item ? item.name : `No ${field.name} selected`}
                </div>
                {item && (
                  <div className="cmb-review__sub">
                    {grams}g · {field.name}
                  </div>
                )}
              </div>
              {item ? (
                <div className="cmb-review__price">
                  <Currency value={(item.price ?? 0) * grams} />
                </div>
              ) : (
                <span className="cmb-review__add" onClick={() => goToStep(index)}>
                  Add
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="cmb-review__totals">
        <span className="cmb-review__totals-label">Meal totals</span>
        <MacroTotals
          totals={totals}
          labels={MACRO_NAMES}
          incomplete={hasIncompleteMacros(selections)}
        />
      </div>

      <div className="cmb-review__instructions">
        <Textarea
          label="Special instructions (optional)"
          name="instructions"
          value={instructions}
          placeholder="E.g. no onions, extra spicy..."
          rows={2}
          bordered
          onChange={({ value }) => setInstructions(value)}
        />
      </div>

      <div className="cmb-review__qty">
        <span>Quantity</span>
        <QuantitySelector
          quantity={qty}
          onIncrement={() => setQty((q) => Math.min(20, q + 1))}
          onDecrement={() => setQty((q) => Math.max(1, q - 1))}
        />
      </div>

      <div className="cmb-review__price-card">
        <span className="cmb-review__price-label">
          Total ({qty} × <Currency value={subTotal} />)
        </span>
        <span className="cmb-review__price-total">
          <Currency value={total} />
        </span>
      </div>
    </div>
  );
};

/* ── AddedScreen ──────────────────────────────────────────────────────────── */

const AddedScreen = ({ summary, cartTotal, onBuildAnother, onViewCart }) => (
  <div className="cmb-status">
    <div className="cmb-status__icon cmb-status__icon--success">
      <SuccessIcon width={36} height={36} />
    </div>
    <div className="cmb-status__title">Added to your cart</div>
    <span className="cmb-status__body">{summary}</span>
    <div className="cmb-status__total">
      <span>Cart total</span>
      <span className="cmb-status__total-value">
        <Currency value={cartTotal} />
      </span>
    </div>
    <div className="cmb-status__actions">
      <Button secondary large onClick={onBuildAnother}>
        Build another
      </Button>
      <Button large onClick={onViewCart}>
        <CartIcon width={18} height={18} />
        <span>View cart</span>
      </Button>
    </div>
  </div>
);

/* ── EmptyScreen ──────────────────────────────────────────────────────────── */

const EmptyScreen = ({ onClose, onBrowseMenu }) => (
  <div className="cmb-status">
    <img src="/images/empty-menu.png" alt="" className="cmb-status__image" />
    <div className="cmb-status__title">Meal builder is unavailable</div>
    <span className="cmb-status__body">
      No build-your-own options are loaded right now. Our ready-made meals are still available, and
      the builder will be back shortly.
    </span>
    <div className="cmb-status__actions">
      <Button secondary large onClick={onClose}>
        Close
      </Button>
      <Button large onClick={onBrowseMenu}>
        Browse menu
      </Button>
    </div>
  </div>
);

/* ── CreateMeal ───────────────────────────────────────────────────────────── */

export const CreateMeal = ({ onCompactChange }) => {
  const { setShowCreateMealModal } = useAppNavigation();
  const { customFields } = useMenus();
  const { addToCart, cartTotal, setCartOpen } = useCart();

  const activeFields = useMemo(
    () => (customFields ?? []).filter((f) => f.active !== false),
    [customFields]
  );

  const initSelections = useMemo(() => {
    const s = {};
    activeFields.forEach((f) => {
      s[f.id] = { item: null, grams: 100 };
    });
    return s;
  }, [activeFields]);

  const [step, setStep] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [addedSummary, setAddedSummary] = useState('');
  const [selections, setSelections] = useState({});
  const [instructions, setInstructions] = useState('');

  useEffect(() => {
    setSelections(initSelections);
  }, [initSelections]);

  const isCompact = added || !activeFields.length;
  useEffect(() => {
    onCompactChange?.(isCompact);
    // eslint-disable-next-line
  }, [isCompact]);

  const isReviewStep = step === activeFields.length;

  const subTotal = useMemo(() => calcSubTotal(selections), [selections]);
  const total = useMemo(() => +(subTotal * qty).toFixed(2), [subTotal, qty]);
  const railTotals = useMemo(() => aggregateMacros(selections), [selections]);
  const hasSelection = useMemo(() => Object.values(selections).some((s) => s?.item), [selections]);

  const currentField = activeFields[step];
  const currentSel = currentField
    ? (selections[currentField.id] ?? { item: null, grams: 100 })
    : null;
  const canNext = isReviewStep || !!currentSel?.item;
  const stepCost = currentSel?.item ? currentSel.item.price * currentSel.grams : null;

  const close = () => setShowCreateMealModal(false);

  const go = (n) => setStep(n);

  const setItem = (fieldId, item) =>
    setSelections((s) => ({ ...s, [fieldId]: { ...s[fieldId], item } }));

  const setGrams = (fieldId, grams) =>
    setSelections((s) => ({ ...s, [fieldId]: { ...s[fieldId], grams } }));

  const handleSkip = () => {
    setItem(currentField.id, null);
    go(step + 1);
  };

  const handleAddToCart = () => {
    let description = '';
    activeFields.forEach((field) => {
      const { item, grams } = selections[field.id] ?? {};
      if (item) {
        description = `${description}${description ? ', ' : ''}${item.name} ${grams}g`;
      }
    });

    addToCart({
      id: uuidv4().toString(),
      name: 'Custom Meal',
      type: 'custom-meal',
      description,
      specialInstructions: instructions.trim() || null,
      imageUrl: '/images/menu-items/generic.png',
      price: subTotal,
      quantity: qty
    });

    setAddedSummary(`${qty} × Custom Meal: ${description}.`);
    setAdded(true);
  };

  const resetAndClose = () => {
    setAdded(false);
    setSelections(initSelections);
    setQty(1);
    setStep(0);
    setInstructions('');
    close();
  };

  const buildAnother = () => {
    setAdded(false);
    setSelections(initSelections);
    setQty(1);
    setStep(0);
    setInstructions('');
  };

  const viewCart = () => {
    setAdded(false);
    setCartOpen(true);
    resetAndClose();
  };

  if (!activeFields.length) {
    return (
      <div className="cmb">
        <EmptyScreen onClose={close} onBrowseMenu={close} />
      </div>
    );
  }

  if (added) {
    return (
      <div className="cmb">
        <AddedScreen
          summary={addedSummary}
          cartTotal={cartTotal}
          onBuildAnother={buildAnother}
          onViewCart={viewCart}
        />
      </div>
    );
  }

  return (
    <div className="cmb">
      <button type="button" className="cmb__close" onClick={close}>
        <CloseIcon width={16} height={16} />
      </button>
      <div className="cmb-rail">
        <div className="cmb-rail__title">Create your meal</div>
        <div className="cmb-rail__steps">
          {activeFields.map((field, index) => {
            const sel = selections[field.id];
            const state = index === step ? 'current' : index < step ? 'done' : 'upcoming';
            const meta =
              state === 'current'
                ? 'Choosing now'
                : state === 'done'
                  ? sel?.item
                    ? `${sel.item.name} · ${sel.grams}g`
                    : 'Skipped'
                  : null;
            const price = state === 'done' && sel?.item ? sel.item.price * sel.grams : null;
            return (
              <RailStep
                key={field.id}
                index={index}
                label={field.name}
                state={state}
                meta={meta}
                price={price}
                onClick={() => go(index)}
              />
            );
          })}
          <RailStep
            index={activeFields.length}
            label="Review"
            state={isReviewStep ? 'current' : 'upcoming'}
            meta={isReviewStep ? 'Choosing now' : null}
          />
        </div>
        <div className="cmb-rail__spacer" />
        <div className="cmb-rail__totals">
          <div className="cmb-rail__totals-head">
            <span>Running total</span>
            <span className="cmb-rail__totals-price">
              <Currency value={subTotal} />
            </span>
          </div>
          <MacroTotals
            totals={railTotals}
            labels={RAIL_MACRO_LABELS}
            incomplete={hasIncompleteMacros(selections)}
          />
          {!isReviewStep && !currentSel?.item && step === 0 && (
            <span className="cmb-rail__hint">
              Pick your first item to start. The total updates as you go.
            </span>
          )}
        </div>
      </div>

      <div className="cmb-main">
        {!isReviewStep && currentField ? (
          <>
            <div className="cmb-main__head">
              <div>
                <h2>{currentField.name}</h2>
                <p>
                  Choose your {currentField.name.toLowerCase()}. Priced per 100 g, portion set after
                  you pick.
                </p>
              </div>
            </div>

            <div className={`cmb-main__scroll${currentSel?.item ? ' has-selection' : ''}`}>
              <div className="cmb-food-grid">
                {(currentField.fieldItems ?? []).map((item) => (
                  <FoodCard
                    key={item.id}
                    item={item}
                    categoryImageUrl={currentField.imageUrl}
                    selected={currentSel?.item?.id === item.id}
                    onSelect={(food) =>
                      setItem(currentField.id, currentSel?.item?.id === food.id ? null : food)
                    }
                  />
                ))}
              </div>
              {currentSel?.item && (
                <div className="cmb-main__selected-summary">
                  <div className="cmb-main__selected-summary-icon">
                    {currentSel.item.imageUrl || currentField.imageUrl ? (
                      <img src={currentSel.item.imageUrl ?? currentField.imageUrl} alt="" />
                    ) : (
                      <span>{currentSel.item.name?.[0]}</span>
                    )}
                  </div>
                  <span className="cmb-main__selected-summary-name">{currentSel.item.name}</span>
                  <span className="cmb-main__selected-summary-price">
                    <Currency value={currentSel.item.price * 100} />
                    /100g
                  </span>
                  <button
                    type="button"
                    className="cmb-main__selected-summary-cancel"
                    onClick={() => setItem(currentField.id, null)}
                    aria-label="Cancel selection"
                  >
                    <CloseIcon width={16} height={16} />
                  </button>
                </div>
              )}
            </div>

            <div className="cmb-main__action-bar">
              {currentSel?.item ? (
                <>
                  <GramSelector
                    compact
                    key={currentSel.item.id}
                    grams={currentSel.grams}
                    onChange={(g) => setGrams(currentField.id, g)}
                  />
                  {stepCost != null && (
                    <span className="cmb-main__cost-preview">
                      Adds{' '}
                      <strong>
                        <Currency value={stepCost} />
                      </strong>{' '}
                      to your meal
                    </span>
                  )}
                </>
              ) : (
                <div className="cmb-main__portion-placeholder">
                  <span>
                    Portion size appears here once you've chosen a {currentField.name.toLowerCase()}
                    .
                  </span>
                </div>
              )}
              <div className="cmb-main__action-row">
                {step > 0 && (
                  <Button secondary onClick={() => go(step - 1)}>
                    Back
                  </Button>
                )}
                <Button tertiary className="cmb-main__skip-btn" onClick={handleSkip}>
                  Skip
                </Button>
                <div className="cmb-main__action-spacer" />
                <Button large onClick={() => go(step + 1)} disabled={!canNext}>
                  <span>Continue</span>
                  <ChevronRightIcon width={18} height={18} />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="cmb-main__head">
              <div>
                <h2>Review your meal</h2>
                <p>Check everything looks right before it goes in the cart.</p>
              </div>
            </div>

            <div className="cmb-main__scroll">
              <ReviewStep
                customFields={activeFields}
                selections={selections}
                qty={qty}
                setQty={setQty}
                subTotal={subTotal}
                total={total}
                goToStep={go}
                instructions={instructions}
                setInstructions={setInstructions}
              />
            </div>

            <div className="cmb-main__footer">
              {!hasSelection && (
                <span className="cmb-main__empty-hint">
                  Select at least one item to add it to your cart.
                </span>
              )}
              <div className="cmb-main__footer-row">
                <Button secondary large onClick={() => go(step - 1)}>
                  Back
                </Button>
                <Button large onClick={handleAddToCart} disabled={!hasSelection}>
                  <CartIcon width={18} height={18} />
                  <span>
                    Add to cart · <Currency value={total} />
                  </span>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
